/*
 * Copyright (c) 2026.
 * # Autor: TECINCO TECNOLOGIA
 * # Dev: Alejandro Meriño Rodrìguez
 * # Versión: 2.0.0
 */

import {inject, Injectable} from "@angular/core";
import {PartService} from "@serv/part.service";
import {Part} from "@interface/part";
import {ApiPaginatedQueryParams} from "@serv/pagination.service";
import {firstValueFrom} from "rxjs";
import {timeout} from "rxjs/operators";
import {
    AddPartsToOsBudgetResponse,
    F_Part_By_Code,
    F_Part_By_Description, PartItemRequest,
    PartSelectionDialogData, ProcessedPartResult, R_ValidateStock
} from '@llm/models/schemas/agents/agree_parts_to_os_budget/interfaces';
import {ToolsAiDialogsService} from "@llm/service/tools_ai-dialogs.service";
import {MkgOS} from "@class/mkg-os";
import {PlayMessageService} from "@llm/service/play_message.service";
import {RoStateGroupPipe} from "@shared/pipes/ro-state-group.pipe";
import {ToolResponse} from '@llm/models/schemas/agents/tools_utils';
import {LlmLoggingService} from "@llm/service/llm-logging.service";
import {MkgOSPart} from "@class/mkg-os-part";
import {partStates} from '@app/shared/lists/part-states';
import {RO_LOG_ACTION_TYPES} from "@interface/os-logs";
import {RoLogsService} from "@serv/ro-logs.service";
import {Utilities} from "@class/utilities";
import {StockController} from "@app/controllers/stock.controller";
import {DataService} from "@app/service/data.service";
import {SessionOSservice} from "@serv/session/session-os.service";


@Injectable({providedIn: 'root'})
export class HelpersAgent {
    private _partService = inject(PartService);
    private _toolsAiDialogsService = inject(ToolsAiDialogsService);
    private _playMessageService = inject(PlayMessageService);
    private _roStateGroupPipe = inject(RoStateGroupPipe);
    private _llmLoggingService = inject(LlmLoggingService);
    private _roLogsService = inject(RoLogsService);
    private _stockController = inject(StockController);
    private _dataService = inject(DataService);
    private _osSectionService = inject(SessionOSservice);

    async closeSession(idOs: string): Promise<void> {
        try {
            if (idOs) {
                console.warn('closed session', idOs)
                await firstValueFrom(this._osSectionService.forceCloseAllSessions(idOs));
            }
        } catch (error) {
            console.error(error);
        }
    }

    isValidObjectId(id: string): boolean {
        return /^[0-9a-fA-F]{24}$/.test(id);
    }



    /**
     * Busca peça por código (busca exata, único resultado)
     * @param code Código da peça
     * @returns Objeto com resultado da busca
     */
    async _findPartByCode(code: string): Promise<F_Part_By_Code> {
        try {
            const result = await firstValueFrom(
                    this._partService.getPaginated({
                    search: code,
                    all: 0,
                    limit: 1
                })
            );

            if (!result.docs || result.docs.length === 0) {
                return {found: false, part: null, reason: 'not_found'};
            }

            const part: Part = result.docs[0];

            if (!part) {
                return {found: false, part: null, reason: 'not_found'};
            }

            if (part.available === 0) {
                return {found: false, part: null, reason: 'inactive'};
            }

            return {found: true, part: part};
        } catch (error) {
            console.error('[Tool_add_parts_os_budget] Erro ao buscar peça por código:', code, error);
            return {found: false, part: null, reason: 'error'};
        }
    }

    /**
     * Busca peça por descrição (pode retornar múltiplos resultados)
     * @param description Descrição para busca
     * @param ro OS de destino (para contexto)
     * @returns Objeto com resultado da busca
     */
    async findPartByDescription(description: string, ro: MkgOS): Promise<F_Part_By_Description> {
        try {
            const result = await firstValueFrom(
                this._partService.getPaginated({
                    search: description,
                    all: 0,
                    limit: 10
                })
            );

            const parts: Part[] = result.docs.filter(p => p.available !== 0);

            if (parts.length === 0) return {found: false, part: null, reason: 'not_found'};

            if (parts.length === 1) return {found: true, part: parts[0]};

            const selectedPart: Part = await this._openPartSelectionDialog(parts, description);

            if (!selectedPart) return {found: false, part: null, reason: 'cancelled'};

            return {found: true, part: selectedPart};

        } catch (error) {
            return {found: false, part: null, reason: 'error'};
        }
    }

    /**
     * Diálogo de seleção de peças quando busca retorna múltiplos resultados
     * @param parts Lista de peças encontradas
     * @param description Descrição original da busca
     * @returns Peça selecionada ou null se cancelado
     */
    async _openPartSelectionDialog(parts: Part[], description: string): Promise<Part | null> {
        try {
            await this._playMessageService.playMessage(
                `Encontramos várias peças com a descrição "${description}". Por favor, selecione a peça correta no diálogo que foi aberto.`,
                true
            );

            const dialogData: PartSelectionDialogData = {
                items: parts.map(part => ({
                    id: part.id,
                    name: `${part.code} - ${part.description}`,
                    descriptor: this._buildPartDescriptor(part)
                })),
                icon: 'build',
                nameClass: 'peça',
                references: {
                    positive: 'Selecionar',
                    negative: 'Fechar janela'
                }
            };

            const selectedId = await firstValueFrom(
                this._toolsAiDialogsService.openDataSelectionDialog(dialogData)
            );

            if (selectedId === 'defaultItem' || !selectedId) {
               console.warn('[Tool_add_parts_os_budget] Usuário cancelou seleção de peça');
                return null;
            }

            const selectedPart = parts.find(p => p.id === selectedId);

            if (selectedPart) {
                console.log('[Tool_add_parts_os_budget] Peça selecionada:', selectedPart.code);
            }

            return selectedPart || null;

        } catch (error) {
            console.error('[Tool_add_parts_os_budget] Erro no diálogo de seleção:', error);
            return null;
        }
    }

    /**
     * Constrói descriptor informativo para peça no diálogo
     * @param part Peça
     * * brand é um ObjectId (string), não um objeto
     * * Se precisar da descrição da marca, seria necessário buscar o PartBrand
     * @returns String com informações relevantes
     */
    private _buildPartDescriptor(part: Part): string {
        const parts: string[] = [];

        if (part.brand) parts.push(`ID Marca: ${part.brand}`);

        if (part.stock !== undefined && part.stock !== null) parts.push(`Estoque: ${part.stock}`);

        if (part.saleValue) parts.push(`R$ ${(part.saleValue).toFixed(2)}`);

        return parts.join(' | ') || 'Peça';
    }


    /**
     * Valida estado da OS para permitir adição de peças
     * @param ro OS a validar
     * @returns Objeto com validação e mensagem de erro (se aplicável)
     */
    validateOSState(ro: MkgOS): ToolResponse {
        const roState = this._roStateGroupPipe.transform(ro.currentState);

        if (['ended', 'closed', 'rejected'].includes(roState)) {
            return this.errorResponse(
                `Não é possível adicionar peças a uma OS ${roState}. A OS deve estar em estado de orçamento ou aberta.`,
                '_validateOSState()'
            );
        }

        return {status: 'ok'};
    }

    /**
     * Consolida resposta de todas as peças processadas.
     * Calcula contagens de sucesso, erro, somas a existentes e cancelamentos.
     * Constrói listas detalhadas para cada categoria (adicionadas, somadas, canceladas, erros).
     * Monta uma mensagem descritiva baseada no cenário (todas somadas, misto, todas novas).
     * Determina o status final da operação ('ok', 'error', 'partial', 'pending') baseado nos resultados.
     * Retorna um ToolResponse contendo a mensagem consolidada e um objeto de resposta detalhado com resumo.
     * 
     * @param results Resultados individuais do processamento de cada peça.
     * @param ro OS processada para contexto na mensagem.
     * @returns ToolResponse consolidado com status, motivo e dados detalhados.
     */
    consolidateResponse(results: ProcessedPartResult[], ro: MkgOS): ToolResponse {
        const successCount = results.filter(r => r.success).length;
        const errorCount = results.length - successCount;
        const summedToExistingCount = results.filter(r => r.action === 'summed_to_existing').length;
        const cancelledCount = results.filter(r => r.action === 'cancelled').length;

        const addedParts = results
            .filter(r => r.success)
            .map(r => `${r.partCode} (${String(r.quantity)}x)`);

        const summedParts = results
            .filter(r => r.action === 'summed_to_existing')
            .map(r => `${r.partCode}: somada ${String(r.quantity)} à existente (total: ${String(r.existingPartQuantity)})`);

        const cancelledParts = results
            .filter(r => r.action === 'cancelled')
            .map(r => `${r.partCode}: cancelada pelo usuário`);

        const errorParts = results
            .filter(r => !r.success && !r.isRepeated && r.action !== 'summed_to_existing' && r.action !== 'cancelled')
            .map(r => `${r.partCode || 'N/A'}: ${r.error}`);

        const totalRelatedItems = results
            .filter(r => r.success && r.relatedItemsCount)
            .reduce((sum, r) => sum + (r.relatedItemsCount || 0), 0);

        let message = '';

        if (successCount > 0) {
            if (summedToExistingCount > 0 && addedParts.length === summedToExistingCount) {
                message += `${summedToExistingCount} peça(s) somada(s) às existentes na OS ${ro.codeSystem}: ${summedParts.join(', ')}.`;
            } else if (summedToExistingCount > 0) {
                message += `${successCount} peça(s) processada(s) com sucesso na OS ${ro.codeSystem}: ${addedParts.join(', ')}. ` +
                    `Destas, ${summedToExistingCount} foram somadas às existentes.`;
            } else {
                message += `${successCount} peça(s) adicionada(s) com sucesso à OS ${ro.codeSystem}: ${addedParts.join(', ')}.`;
            }

            if (totalRelatedItems > 0) {
                message += ` Itens relacionados adicionados automaticamente: ${totalRelatedItems}.`;
            }
        }

        if (cancelledParts.length > 0) message += ` Canceladas pelo usuário: ${cancelledParts.join('; ')}.`;

        if (errorParts.length > 0) message += ` Erros: ${errorParts.join('; ')}.`;

        const requiresUserAction = results.some(r => r.requiresNcmCorrection);

        let status: 'ok' | 'error' | 'pending' | 'partial';

        if (successCount === 0 && errorCount === results.length) {
            status = 'error';
            if (!message) {
                message = `Nenhuma peça pôde ser processada. Erros: ${errorParts.join('; ')}`;
            }
        } else if (successCount === results.length && errorCount === 0 && cancelledCount === 0) {
            status = 'ok';
        } else if (successCount > 0 && (errorCount > 0 || cancelledCount > 0)) {
            status = 'partial';
            message = `${successCount} de ${results.length} peça(s) processada(s) com sucesso. ` +
                (errorCount > 0 ? `Erros em ${errorCount} peça(s): ${errorParts.join('; ')}. ` : '') +
                (cancelledCount > 0 ? `Canceladas pelo usuário: ${cancelledCount}.` : '');
        } else if (requiresUserAction || cancelledCount > 0) {
            status = 'pending';
        } else {
            status = 'ok';
        }

        const response: AddPartsToOsBudgetResponse = {
            success: successCount > 0,
            message,
            os_id: ro.id,
            os_code: String(ro.codeSystem),
            added_parts: addedParts,
            missing_parts: errorParts,
            repeated_parts: summedParts,
            error_parts: errorParts,
            related_items_added: totalRelatedItems,
            requires_user_action: requiresUserAction,
            summary: {
                total_requested: results.length,
                total_added: successCount,
                total_errors: errorCount,
                total_summed_to_existing: summedToExistingCount
            }
        };

        console.log('[Tool_add_parts_os_budget] Resposta consolidada:', response);

        return {
            status,
            reason: message,
            data: response
        };
    }

    /**
     * Valida quantidade vs estoque disponível
     * 
     * ✅ Usa o StockController existente para validação consistente com o fluxo manual
     * ✅ Verifica company.controlStock antes de validar
     * ✅ Usa availableStock (stock - osStock - orderStock) em vez de stock bruto
     * ✅ Permite adição com estoque negativo (erro de dados) mas loga aviso
     * 
     * @param part Peça
     * @param requestedQuantity Quantidade solicitada
     * @returns Validação e ação tomada
     */
    async validateStock(part: Part, requestedQuantity: number): Promise<R_ValidateStock> {
        // ✅ Verificar se empresa controla estoque
        if (!this._dataService.company.controlStock) {
            return {valid: true};  // Empresa não controla estoque
        }

        // ✅ Usar availableStock (já calculado pelo StockController)
        // availableStock = stock - osStock - orderStock
        const availableStock = part.availableStock || 0;

        // ✅ Estoque negativo é erro de dados - permitir mas logar
        if (availableStock < 0) {
            console.warn(
                `[validateStock] ⚠️ Peça ${part.code} (${part.description}) ` +
                `tem availableStock negativo: ${availableStock}. ` +
                `Isso indica erro de dados no banco. Verifique as movimentações de estoque.`
            );
            return {valid: true};  // Permite adição
        }

        // ✅ Estoque zero - bloquear
        if (availableStock === 0) {
            return {valid: false, action: 'cancelled'};
        }

        // ✅ Quantidade dentro do disponível
        if (requestedQuantity <= availableStock) {
            return {valid: true};
        }

        // ✅ Quantidade excede estoque → oferece opções
        const dialogData: PartSelectionDialogData = {
            items: [
                {
                    id: 'max',
                    name: `Usar máximo disponível (${availableStock})`,
                    descriptor: 'Estoque atual da peça'
                },
                {
                    id: 'cancel',
                    name: 'Cancelar adição',
                    descriptor: 'Quantidade excede estoque'
                }
            ],
            icon: 'warning',
            nameClass: 'ação',
            references: {
                positive: 'Selecionar',
                negative: 'Fechar'
            }
        };

        await this._playMessageService.playMessage(
            `A quantidade solicitada (${requestedQuantity}) excede o estoque disponível (${availableStock}). ` +
            `Por favor, selecione uma opção no diálogo que foi aberto.`,
            true
        );

        const selectedId = await firstValueFrom(
            this._toolsAiDialogsService.openDataSelectionDialog(dialogData)
        );

        console.log('[Tool_add_parts_os_budget] Seleção para validação de estoque:', selectedId);

        if (selectedId === 'max') return {valid: true, action: 'use_max'};

        return {valid: false, action: 'cancelled'};
    }


    /**
     * Helper para resposta de erro
     * @param reason Mensagem de erro
     * @param method
     * @returns ToolResponse de erro
     */
    errorResponse(reason: string, method?: string): ToolResponse {
        this._llmLoggingService.info(reason, {}, '[HELPER_AGENT_ADD_PARTS_OS]', method)
        return {
            status: 'error',
            reason
        };
    }

    /**
     * Verifica se peça já existe na OS
     * @param ro OS
     * @param part Peça a verificar
     * @returns Peça existente na OS ou null
     */
    checkRepeatedPart(ro: MkgOS, part: Part): MkgOSPart | null {
        const existingPart = ro.parts.find(p => p.part.id === part.id);
        return existingPart || null;
    }

    /**
     * Determina status da peça baseado no estado da OS
     * @param ro OS
     * - Orçamento: peça em espera de aprovação
     * - OS Aberta: peça já aprovada
     * @returns ID do status (partStates)
     */
    getPartStatus(ro: MkgOS): number {
        if (ro.isBudget) {
            return partStates.waiting.id;
        }
        return partStates.approved.id;
    }

    /**
     * Registra log de auditoria para operação de peça
     * @param ro OS
     * @param part Peça
     * @param action Tipo de ação (add, update, remove)
     */
    async createAuditLog(ro: MkgOS, part: Part, action: 'add' | 'update' | 'remove'): Promise<void> {
        try {
            const actionDescriptions = {
                add: `Adicionando peça: ${part.description} - ${part.code}`,
                update: `Atualizando peça: ${part.description} - ${part.code}`,
                remove: `Removendo peça: ${part.description} - ${part.code}`
            };

            await this._roLogsService.create({
                actionType: action === 'add' ? RO_LOG_ACTION_TYPES.ADD_PART : RO_LOG_ACTION_TYPES.UPDATE_PART,
                osId: ro.id,
                statusInTheLogRecord: ro.currentState,
                reason: actionDescriptions[action]
            });

           console.log('[HELPER_AGENT] Log de auditoria registrado:', part.code);
        } catch (error) {
           console.error('[HELPER_AGENT] Erro ao criar log de auditoria:', error);
        }
    }



    /**
     * @deprecated
     * Busca peça por código de fábrica (pode retornar múltiplos resultados)
     * @param codeFabric Código de fábrica da peça
     * @returns Objeto com resultado da busca
     */
    private async _findPartByFabricCode(codeFabric: string): Promise<F_Part_By_Description> {
        try {
            const result = await firstValueFrom(
                this._partService.getPaginated({
                    search: codeFabric,
                    all: 0,
                    limit: 10
                })
            );

            const parts = result.docs.filter(p => p.available !== 0);

            console.log('[Tool_add_parts_os_budget] Busca por código de fábrica retornou:', parts.length, 'resultados');

            if (parts.length === 0) {
                return {found: false, part: null, reason: 'not_found'};
            }

            if (parts.length === 1) {
                return {found: true, part: parts[0]};
            }

            // Múltiplas peças → Abre diálogo de seleção
            console.log('[Tool_add_parts_os_budget] Múltiplas peças encontradas por código de fábrica, abrindo diálogo de seleção');
            const selectedPart = await this._openPartSelectionDialog(parts, `Cód. Fábrica: ${codeFabric}`);

            if (!selectedPart) {
                return {found: false, part: null, reason: 'cancelled'};
            }

            return {found: true, part: selectedPart};

        } catch (error) {
            console.error('[Tool_add_parts_os_budget] Erro ao buscar peça por código de fábrica:', error);
            return {found: false, part: null, reason: 'error'};
        }
    }

    /**
     * @deprecated
     * Busca peça por código EAN (pode retornar múltiplos resultados)
     * @param codeEAN Código EAN da peça
     * @returns Objeto com resultado da busca
     */
    private async _findPartByEAN(codeEAN: string): Promise<F_Part_By_Description> {
        try {
            const searchParams: ApiPaginatedQueryParams = {
                search: codeEAN.trim(),
                all: 0,
                limit: 10
            }

            const result = await firstValueFrom(
                this._partService.getPaginated(searchParams).pipe(
                    timeout({first: 10000})
                )
            );

            const parts = result.docs.filter(p => p.available !== 0);

            console.log('Busca por EAN retornou:', parts.length, 'resultados');

            if (parts.length === 0) return {found: false, part: null, reason: 'not_found'};

            if (parts.length === 1) return {found: true, part: parts[0]};

            // Múltiplas peças → Abre diálogo de seleção
            console.log('Múltiplas peças encontradas por EAN, abrindo diálogo de seleção');
            const selectedPart = await this._openPartSelectionDialog(parts, `EAN: ${codeEAN}`);

            if (!selectedPart) return {found: false, part: null, reason: 'cancelled'};

            return {found: true, part: selectedPart};

        } catch (error) {
            console.error('Erro ao buscar peça por EAN:', error);
            return {found: false, part: null, reason: 'error'};
        }
    }



    async _validateSearchResult(part:F_Part_By_Code){

    }

}

